import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	IconButton,
	MenuItem,
	Paper,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface MethodologyRow {
	sourceKey: string;
	sourceName: string;
	scope: number;
	method: string;
	factorSource: string;
	gwpSet: string;
	assumptions: string | null;
}
interface EvidenceRow {
	evidenceID: number;
	sourceKey: string;
	year: number;
	title: string;
	evidenceType: string;
	reference: string | null;
	addedBy: string | null;
	addedAt: string;
}
interface AuditRow {
	eventID: number;
	actor: string | null;
	action: string;
	subject: string | null;
	detail: string | null;
	createdAt: string;
}

const EVIDENCE_TYPES = [
	"invoice",
	"meter-reading",
	"utility-statement",
	"contract",
	"supplier-declaration",
	"spreadsheet",
	"other",
];

/** Assurance workspace: methodology register, evidence register, audit trail. */
const Assurance: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [tab, setTab] = useState(0);
	const [methodology, setMethodology] = useState<MethodologyRow[]>([]);
	const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
	const [audit, setAudit] = useState<AuditRow[]>([]);
	const [msg, setMsg] = useState<string | null>(null);
	const [notes, setNotes] = useState<Record<string, string>>({});
	const [ev, setEv] = useState({
		sourceKey: "fuels",
		year: String(new Date().getFullYear() - 1),
		title: "",
		evidenceType: "invoice",
		reference: "",
	});

	const call = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});
			const data = await res.json().catch(() => ({}));
			return { ok: res.ok, data: data.Data || data };
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		const [m, e, a] = await Promise.all([
			call("/api/assurance/methodology"),
			call("/api/assurance/evidence"),
			call("/api/assurance/audit-log"),
		]);
		if (m.ok) {
			setMethodology(m.data.register || []);
			const n: Record<string, string> = {};
			for (const row of m.data.register || [])
				n[row.sourceKey] = row.assumptions || "";
			setNotes(n);
		}
		if (e.ok) setEvidence(e.data.evidence || []);
		if (a.ok) setAudit(a.data.events || []);
	}, [call]);

	useEffect(() => {
		load();
	}, [load]);

	const saveNotes = async (sourceKey: string) => {
		const r = await call("/api/assurance/methodology", {
			method: "POST",
			body: JSON.stringify({ sourceKey, assumptions: notes[sourceKey] }),
		});
		setMsg(r.data.Message || r.data.message || null);
	};

	const addEvidence = async () => {
		const r = await call("/api/assurance/evidence", {
			method: "POST",
			body: JSON.stringify(ev),
		});
		setMsg(r.data.Message || r.data.message || null);
		if (r.ok) {
			setEv({ ...ev, title: "", reference: "" });
			await load();
		}
	};

	return (
		<Box maxWidth={1000} margin="0 auto" padding={3}>
			<Typography variant="h5" gutterBottom>
				Assurance
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				Everything an assurance practitioner asks for: how each figure
				is calculated, the evidence behind it, and who changed what.
			</Typography>

			{msg && (
				<Alert
					severity="info"
					sx={{ my: 1 }}
					onClose={() => setMsg(null)}
				>
					{msg}
				</Alert>
			)}

			<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
				<Tab label="Methodology" />
				<Tab label="Evidence" />
				<Tab label="Audit trail" />
			</Tabs>

			{tab === 0 && (
				<Paper
					elevation={0}
					sx={{ p: 2, border: "1px solid rgba(0,23,28,0.10)" }}
				>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Scope</TableCell>
								<TableCell>Source</TableCell>
								<TableCell>Method & factors</TableCell>
								<TableCell width="35%">
									Company assumptions / notes
								</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{methodology.map((m) => (
								<TableRow key={m.sourceKey}>
									<TableCell>S{m.scope}</TableCell>
									<TableCell>{m.sourceName}</TableCell>
									<TableCell>
										<Typography variant="body2">
											{m.method}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											{m.factorSource} · {m.gwpSet}
										</Typography>
									</TableCell>
									<TableCell>
										<TextField
											fullWidth
											multiline
											maxRows={3}
											size="small"
											placeholder="e.g. estimates used for Q1; excludes site X"
											value={notes[m.sourceKey] ?? ""}
											onChange={(e) =>
												setNotes({
													...notes,
													[m.sourceKey]:
														e.target.value,
												})
											}
										/>
									</TableCell>
									<TableCell>
										<IconButton
											size="small"
											aria-label="Save notes"
											onClick={() =>
												saveNotes(m.sourceKey)
											}
										>
											<SaveOutlinedIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
			)}

			{tab === 1 && (
				<>
					<Paper
						elevation={0}
						sx={{
							p: 2,
							mb: 2,
							border: "1px solid rgba(0,23,28,0.10)",
						}}
					>
						<Box
							display="flex"
							gap={2}
							flexWrap="wrap"
							alignItems="center"
						>
							<TextField
								select
								label="Source"
								size="small"
								value={ev.sourceKey}
								onChange={(e) =>
									setEv({ ...ev, sourceKey: e.target.value })
								}
								sx={{ minWidth: 210 }}
							>
								{methodology.map((m) => (
									<MenuItem
										key={m.sourceKey}
										value={m.sourceKey}
									>
										{m.sourceName}
									</MenuItem>
								))}
							</TextField>
							<TextField
								label="Year"
								size="small"
								value={ev.year}
								onChange={(e) =>
									setEv({ ...ev, year: e.target.value })
								}
								sx={{ width: 100 }}
							/>
							<TextField
								select
								label="Type"
								size="small"
								value={ev.evidenceType}
								onChange={(e) =>
									setEv({
										...ev,
										evidenceType: e.target.value,
									})
								}
								sx={{ minWidth: 170 }}
							>
								{EVIDENCE_TYPES.map((t) => (
									<MenuItem key={t} value={t}>
										{t}
									</MenuItem>
								))}
							</TextField>
							<TextField
								label="Title"
								size="small"
								value={ev.title}
								onChange={(e) =>
									setEv({ ...ev, title: e.target.value })
								}
								sx={{ minWidth: 220, flexGrow: 1 }}
							/>
							<TextField
								label="Reference / link (optional)"
								size="small"
								value={ev.reference}
								onChange={(e) =>
									setEv({ ...ev, reference: e.target.value })
								}
								sx={{ minWidth: 200 }}
							/>
							<Button
								variant="contained"
								color="secondary"
								disabled={ev.title.trim().length < 3}
								onClick={addEvidence}
							>
								Record evidence
							</Button>
						</Box>
					</Paper>
					<Paper
						elevation={0}
						sx={{ p: 2, border: "1px solid rgba(0,23,28,0.10)" }}
					>
						{evidence.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No evidence recorded yet.
							</Typography>
						) : (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Year</TableCell>
										<TableCell>Source</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Title</TableCell>
										<TableCell>Reference</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{evidence.map((e) => (
										<TableRow key={e.evidenceID}>
											<TableCell>{e.year}</TableCell>
											<TableCell>{e.sourceKey}</TableCell>
											<TableCell>
												{e.evidenceType}
											</TableCell>
											<TableCell>{e.title}</TableCell>
											<TableCell>
												{e.reference || "—"}
											</TableCell>
											<TableCell align="right">
												<IconButton
													size="small"
													aria-label="Remove evidence"
													onClick={async () => {
														await call(
															`/api/assurance/evidence?evidenceId=${e.evidenceID}`,
															{
																method: "DELETE",
															},
														);
														await load();
													}}
												>
													<DeleteOutlineIcon fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</Paper>
				</>
			)}

			{tab === 2 && (
				<Paper
					elevation={0}
					sx={{ p: 2, border: "1px solid rgba(0,23,28,0.10)" }}
				>
					{audit.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No recorded changes yet. Actions like locking a
							baseline, adding factors, spend or evidence appear
							here automatically.
						</Typography>
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>When</TableCell>
									<TableCell>Who</TableCell>
									<TableCell>Action</TableCell>
									<TableCell>Subject</TableCell>
									<TableCell>Detail</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{audit.map((a) => (
									<TableRow key={a.eventID}>
										<TableCell>
											{String(a.createdAt)
												.replace("T", " ")
												.slice(0, 16)}
										</TableCell>
										<TableCell>{a.actor || "—"}</TableCell>
										<TableCell>{a.action}</TableCell>
										<TableCell>
											{a.subject || "—"}
										</TableCell>
										<TableCell>{a.detail || "—"}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			)}
		</Box>
	);
};

export default Assurance;
